import type { VideoItem, VideoCategory, CachedPlaylistData } from '@/types/educaliec';
import { educaliecCategories } from '@/data/educaliec-categories';

// ─── Demo Data ───────────────────────────────────────────────────────────────

/**
 * Returns demo video data for testing when API key is not configured
 */
function getDemoVideos(): VideoItem[] {
  return [
    {
      id: 'demo-1',
      title: 'Introducción a la Topografía - Conceptos Básicos',
      description: 'Aprende los fundamentos de la topografía y sus aplicaciones en la ingeniería civil.\n\nImpartido por: Ing. María González\n\nTemas cubiertos:\n- Definición de topografía\n- Instrumentos básicos\n- Métodos de medición',
      thumbnailUrl: 'https://img.youtube.com/vi/dQw4w9WgXcQ/mqdefault.jpg',
      publishedAt: '2024-01-15T10:00:00Z',
      viewCount: 1250,
      instructor: 'Ing. María González',
      categoryIds: ['topografia']
    },
    {
      id: 'demo-2',
      title: 'Análisis de Suelos - Laboratorio Práctico',
      description: 'Técnicas de laboratorio para el análisis de propiedades del suelo.\n\nPonente: Dr. Carlos Mendoza\n\nContenido:\n- Preparación de muestras\n- Ensayos de granulometría\n- Límites de Atterberg',
      thumbnailUrl: 'https://img.youtube.com/vi/dQw4w9WgXcQ/mqdefault.jpg',
      publishedAt: '2024-01-10T14:30:00Z',
      viewCount: 890,
      instructor: 'Dr. Carlos Mendoza',
      categoryIds: ['geotecnia']
    },
    {
      id: 'demo-3',
      title: 'Diseño de Estructuras de Concreto Armado',
      description: 'Principios fundamentales del diseño estructural con concreto reforzado.\n\nInstructor: Ing. Ana Rodríguez\n\nTemas:\n- Propiedades del concreto\n- Diseño de vigas\n- Diseño de columnas',
      thumbnailUrl: 'https://img.youtube.com/vi/dQw4w9WgXcQ/mqdefault.jpg',
      publishedAt: '2024-01-05T09:15:00Z',
      viewCount: 2100,
      instructor: 'Ing. Ana Rodríguez',
      categoryIds: ['estructuras']
    },
    {
      id: 'demo-4',
      title: 'Gestión de Proyectos de Construcción',
      description: 'Metodologías para la gestión eficiente de proyectos constructivos.\n\nImpartido por: MBA Ing. Roberto Silva\n\nContenido:\n- Planificación de proyectos\n- Control de costos\n- Gestión de riesgos',
      thumbnailUrl: 'https://img.youtube.com/vi/dQw4w9WgXcQ/mqdefault.jpg',
      publishedAt: '2024-01-01T16:45:00Z',
      viewCount: 1680,
      instructor: 'MBA Ing. Roberto Silva',
      categoryIds: ['gestion']
    }
  ];
}

// ─── Configuration ───────────────────────────────────────────────────────────

export interface YouTubeServiceConfig {
  apiKey: string;
  playlistId: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const CACHE_KEY = 'educaliec_videos';

/** Cache TTL: 24 hours in milliseconds */
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

const YOUTUBE_PLAYLIST_ITEMS_URL =
  'https://www.googleapis.com/youtube/v3/playlistItems';
const YOUTUBE_VIDEOS_URL =
  'https://www.googleapis.com/youtube/v3/videos';

// ─── Instructor Extraction ───────────────────────────────────────────────────

const INSTRUCTOR_PATTERNS = [
  /Impartido(?:\s+por)?:\s*(.+)/i,
  /Instructor:\s*(.+)/i,
  /Ponente:\s*(.+)/i,
];

/**
 * Extracts the instructor name from a video description by checking each line
 * against known patterns.
 * Returns the trimmed name on match, null if no pattern matches.
 */
export function extractInstructor(description: string): string | null {
  const lines = description.split('\n');

  for (const line of lines) {
    for (const pattern of INSTRUCTOR_PATTERNS) {
      const match = line.match(pattern);
      if (match?.[1]) {
        return match[1].trim();
      }
    }
  }

  return null;
}

// ─── Category Matching ───────────────────────────────────────────────────────

/**
 * Matches a video against category definitions by checking keywords
 * (case-insensitive) in the video's title, description, and tags.
 * Returns an array of matched category IDs.
 * The "todos" category (empty keywords) is never matched.
 */
export function matchCategories(
  video: { title: string; description: string; tags?: string[] },
  categories: VideoCategory[]
): string[] {
  const matched: string[] = [];
  const searchText = [
    video.title,
    video.description,
    ...(video.tags ?? []),
  ]
    .join(' ')
    .toLowerCase();

  for (const category of categories) {
    if (category.keywords.length === 0) {
      continue;
    }

    const hasMatch = category.keywords.some((keyword) =>
      searchText.includes(keyword.toLowerCase())
    );

    if (hasMatch) {
      matched.push(category.id);
    }
  }

  return matched;
}

// ─── In-Memory Cache Fallback ────────────────────────────────────────────────

let memoryCache: CachedPlaylistData | null = null;

// ─── Cache Helpers (localStorage for persistence across sessions) ────────────

function getCache(playlistId: string): CachedPlaylistData | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return memoryCache?.playlistId === playlistId ? memoryCache : null;

    const cached: CachedPlaylistData = JSON.parse(raw);
    if (cached.playlistId !== playlistId) return null;

    return cached;
  } catch {
    // localStorage unavailable — use memory cache
    if (memoryCache && memoryCache.playlistId === playlistId) {
      return memoryCache;
    }
    return null;
  }
}

function isCacheValid(cached: CachedPlaylistData): boolean {
  return Date.now() - cached.timestamp < CACHE_TTL_MS;
}

function setCache(data: CachedPlaylistData): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    // localStorage unavailable — store in memory
    memoryCache = data;
  }
}

// ─── Deduplication: prevent concurrent identical requests ────────────────────

let inflightRequest: Promise<VideoItem[]> | null = null;

// ─── YouTube API Helpers ─────────────────────────────────────────────────────

interface PlaylistItemSnippet {
  title: string;
  description: string;
  thumbnails: {
    medium?: { url: string };
    default?: { url: string };
  };
  publishedAt: string;
  resourceId: { videoId: string };
}

interface PlaylistItemsResponse {
  items: Array<{ snippet: PlaylistItemSnippet }>;
  nextPageToken?: string;
}

interface VideoStatisticsResponse {
  items: Array<{
    id: string;
    statistics: { viewCount?: string };
  }>;
}

async function fetchAllPlaylistItems(
  apiKey: string,
  playlistId: string
): Promise<PlaylistItemSnippet[]> {
  const allItems: PlaylistItemSnippet[] = [];
  let pageToken: string | undefined;

  do {
    const url = new URL(YOUTUBE_PLAYLIST_ITEMS_URL);
    url.searchParams.set('part', 'snippet');
    url.searchParams.set('maxResults', '50');
    url.searchParams.set('playlistId', playlistId);
    url.searchParams.set('key', apiKey);
    if (pageToken) {
      url.searchParams.set('pageToken', pageToken);
    }

    const response = await fetch(url.toString());

    if (!response.ok) {
      throw response;
    }

    const data: PlaylistItemsResponse = await response.json();
    allItems.push(...data.items.map((item) => item.snippet));
    pageToken = data.nextPageToken;
  } while (pageToken);

  return allItems;
}

async function fetchVideoStatistics(
  apiKey: string,
  videoIds: string[]
): Promise<Map<string, number>> {
  const statsMap = new Map<string, number>();

  // YouTube API allows max 50 IDs per request
  for (let i = 0; i < videoIds.length; i += 50) {
    const batch = videoIds.slice(i, i + 50);
    const url = new URL(YOUTUBE_VIDEOS_URL);
    url.searchParams.set('part', 'statistics');
    url.searchParams.set('id', batch.join(','));
    url.searchParams.set('key', apiKey);

    const response = await fetch(url.toString());

    if (!response.ok) {
      throw response;
    }

    const data: VideoStatisticsResponse = await response.json();
    for (const item of data.items) {
      statsMap.set(item.id, parseInt(item.statistics.viewCount ?? '0', 10));
    }
  }

  return statsMap;
}

// ─── Main Service Function ───────────────────────────────────────────────────

/**
 * Fetches all videos from the configured YouTube playlist, including statistics.
 * 
 * Caching strategy:
 * - Uses localStorage to persist data across sessions (24h TTL)
 * - If cache is valid (< 24h old), returns cached data immediately (0 API calls)
 * - If cache is expired, fetches fresh data from YouTube API
 * - On API error (403 rate limit), falls back to expired cache or demo data
 * - Deduplicates concurrent requests to prevent multiple API calls
 */
export async function fetchPlaylistVideos(
  config?: YouTubeServiceConfig
): Promise<VideoItem[]> {
  const defaultApiKey = 'AIzaSyAOt9pctfFzObD3wiAJjAETt6kwCwRMkNk';
  const defaultPlaylistId = 'PLiD45i4_55hfNsRs9OB2bBEs-K2vm7XsV';
  
  const apiKey =
    config?.apiKey ?? 
    import.meta.env.VITE_YOUTUBE_API_KEY ?? 
    defaultApiKey;
  const playlistId =
    config?.playlistId ?? 
    import.meta.env.VITE_YOUTUBE_PLAYLIST_ID ?? 
    defaultPlaylistId;

  // If using demo key, return sample data
  if (apiKey === 'DEMO_KEY_REPLACE_WITH_REAL_API_KEY') {
    return getDemoVideos();
  }

  if (!apiKey) {
    console.error(
      '[EducaLIEC] Falta la variable de entorno VITE_YOUTUBE_API_KEY. Configúrala en el archivo .env.'
    );
    return getDemoVideos();
  }

  if (!playlistId) {
    console.error(
      '[EducaLIEC] Falta la variable de entorno VITE_YOUTUBE_PLAYLIST_ID. Configúrala en el archivo .env.'
    );
    return getDemoVideos();
  }

  // Check cache — if valid, return immediately (no API call)
  const cached = getCache(playlistId);
  if (cached && isCacheValid(cached)) {
    return cached.videos;
  }

  // Deduplicate: if there's already a request in flight, reuse it
  if (inflightRequest) {
    return inflightRequest;
  }

  // Make the actual API request
  inflightRequest = (async () => {
    try {
      // Step 1: Fetch all playlist items (handles pagination)
      const snippets = await fetchAllPlaylistItems(apiKey, playlistId);

      // Step 2: Batch fetch video statistics
      const videoIds = snippets.map((s) => s.resourceId.videoId);
      const statsMap = await fetchVideoStatistics(apiKey, videoIds);

      // Step 3: Filter out deleted/private videos and map to VideoItem[]
      const validSnippets = snippets.filter((snippet) => {
        const title = snippet.title?.toLowerCase() ?? '';
        // YouTube marks deleted/private videos with these titles
        if (title === 'deleted video' || title === 'private video') {
          return false;
        }
        // Also skip if there's no thumbnail (another indicator of removed videos)
        if (!snippet.thumbnails.medium?.url && !snippet.thumbnails.default?.url) {
          return false;
        }
        return true;
      });

      const videos: VideoItem[] = validSnippets.map((snippet) => {
        const videoId = snippet.resourceId.videoId;
        const thumbnailUrl =
          snippet.thumbnails.medium?.url ??
          snippet.thumbnails.default?.url ??
          '';

        return {
          id: videoId,
          title: snippet.title,
          description: snippet.description,
          thumbnailUrl,
          publishedAt: snippet.publishedAt,
          viewCount: statsMap.get(videoId) ?? 0,
          instructor: extractInstructor(snippet.description),
          categoryIds: matchCategories(
            { title: snippet.title, description: snippet.description },
            educaliecCategories
          ),
        };
      });

      // Step 4: Sort by publishedAt descending
      videos.sort(
        (a, b) =>
          new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
      );

      // Step 5: Cache the result in localStorage (persists 24h)
      const cacheData: CachedPlaylistData = {
        videos,
        timestamp: Date.now(),
        playlistId,
      };
      setCache(cacheData);

      return videos;
    } catch (error) {
      // On 403 (rate limit or domain restriction), serve cached data even if expired
      if (error instanceof Response && error.status === 403) {
        console.warn(
          '[EducaLIEC] API returned 403. This is likely a domain restriction (API key only allows requests from production domain). Using fallback data.'
        );
        if (cached) {
          return cached.videos;
        }
        // No cache at all — return demo data so the page isn't empty
        return getDemoVideos();
      }

      throw error;
    } finally {
      inflightRequest = null;
    }
  })();

  return inflightRequest;
}
